import { useState, useCallback, useRef } from 'react';

export interface APICall {
  id: string;
  timestamp: Date;
  method: string;
  url: string;
  status?: number;
  duration?: number;
  requestBody?: any;
  responseData?: any;
  error?: string;
}

export function useAPIMonitor() {
  const [apiCalls, setApiCalls] = useState<APICall[]>([]);
  const callsRef = useRef<APICall[]>([]);

  const monitoredFetch = useCallback(async (url: string, options?: RequestInit) => {
    const callId = Math.random().toString(36).substr(2, 9);
    const startTime = Date.now();

    const call: APICall = {
      id: callId,
      timestamp: new Date(),
      method: options?.method || 'GET',
      url,
      requestBody: options?.body ? JSON.parse(options.body as string) : undefined
    };

    // Add to tracking
    setApiCalls(prev => [...prev.slice(-19), call]); // Keep last 20 calls
    callsRef.current = [...callsRef.current.slice(-19), call];

    try {
      const response = await fetch(url, options);
      const duration = Date.now() - startTime;

      // Clone response to read body
      const clonedResponse = response.clone();
      let responseData;
      try {
        responseData = await clonedResponse.json();
      } catch {
        responseData = null;
      }

      // Update call with response info
      const updatedCall = {
        ...call,
        status: response.status,
        duration,
        responseData
      };

      setApiCalls(prev =>
        prev.map(c => c.id === callId ? updatedCall : c)
      );

      return response;
    } catch (error) {
      const duration = Date.now() - startTime;

      // Update call with error info
      const updatedCall = {
        ...call,
        duration,
        error: error.message
      };

      setApiCalls(prev =>
        prev.map(c => c.id === callId ? updatedCall : c)
      );

      throw error;
    }
  }, []);

  const clearCalls = useCallback(() => {
    setApiCalls([]);
    callsRef.current = [];
  }, []);

  const getCallStats = useCallback(() => {
    const calls = callsRef.current;
    const successCalls = calls.filter(c => c.status && c.status < 300);
    const errorCalls = calls.filter(c => c.status && c.status >= 400);
    const avgDuration = calls.reduce((sum, c) => sum + (c.duration || 0), 0) / (calls.length || 1);

    return {
      total: calls.length,
      success: successCalls.length,
      errors: errorCalls.length,
      avgDuration: Math.round(avgDuration)
    };
  }, []);

  return {
    apiCalls,
    monitoredFetch,
    clearCalls,
    getCallStats
  };
}